import type { Msg } from "../../types/chat";

/**
 * Plain functions for the ways a channel's message list changes when something arrives over the
 * socket: a new message, a read receipt, an edit, a delete, reaction counts, and a loaded backlog.
 * They take the current list and return the new one (or the SAME list when nothing changed, so a
 * caller can tell with `next === list`). No React, no state, no side effects: easy to test.
 *
 * Moved out of ChatLayout's socket handlers with the same behaviour.
 */

type StripSelfSeen = (m: Msg) => Msg;

// ---------------------------------------------------------------------------
// Duplicate deliveries
// ---------------------------------------------------------------------------

/** What we remember about recent incoming messages of one channel, to drop duplicate deliveries. */
export type IncomingCache = {
  ids: Set<string>;
  recents: { authorId?: string; content: string; ts: number }[];
};

export const emptyIncomingCache = (): IncomingCache => ({ ids: new Set<string>(), recents: [] });

export type IncomingVerdict = "duplicate-id" | "duplicate-content" | "new";

const DUPLICATE_WINDOW_MS = 5000;
const MAX_RECENTS = 30;
const MAX_IDS = 2000;
const KEEP_RECENTS = 200;

/**
 * Decides whether an incoming message is a duplicate delivery and updates the cache.
 *  - "duplicate-id": we have already seen this message id.
 *  - "duplicate-content": the same author sent the same text within 5 s, and this delivery carries
 *    no tempId (so it is not the echo of our own optimistic send). Call-event messages are never
 *    treated as duplicates by content.
 *  - "new": remember it and show it.
 * Mutates `cache` (including swapping in a smaller id set once it grows past 2000 ids).
 */
export function classifyIncoming(
  cache: IncomingCache,
  msg: { msgId?: string; authorId: string; content?: string; kind?: string; tempId?: string; now: number },
): IncomingVerdict {
  const contentNorm = (msg.content || "").trim();
  const { msgId, authorId, kind, tempId, now } = msg;
  if (msgId && cache.ids.has(msgId)) return "duplicate-id";
  const recentSame = kind === "call_event"
    ? undefined
    : cache.recents.find((r) => r.authorId === authorId && r.content === contentNorm && (now - r.ts) < DUPLICATE_WINDOW_MS);
  if (recentSame && !tempId) {
    cache.ids.add(msgId || `${authorId}:${contentNorm}:${now}`);
    return "duplicate-content";
  }
  cache.ids.add(msgId || `${authorId}:${contentNorm}:${now}`);
  cache.recents.push({ authorId, content: contentNorm, ts: now });
  while (cache.recents.length > MAX_RECENTS) cache.recents.shift();
  if (cache.ids.size > MAX_IDS) {
    // keep the ids set bounded
    const keep = new Set<string>();
    cache.recents.slice(-KEEP_RECENTS).forEach((r) => keep.add(`${r.authorId || ""}:${r.content}:${r.ts}`));
    cache.ids = keep;
  }
  return "new";
}

// ---------------------------------------------------------------------------
// List updates
// ---------------------------------------------------------------------------

/**
 * Adds a delivered message. When it is the server's copy of a message we sent (matching tempId) in the
 * channel on screen, it replaces our optimistic copy in place. A message whose id is already in the list
 * is ignored (returns the same list).
 */
export function applyIncoming(
  list: Msg[],
  message: Msg,
  opts: { tempId?: string; isCurrentChannel: boolean; stripSelfSeen: StripSelfSeen },
): Msg[] {
  const msgId = message?.id;
  const nextMsg = opts.stripSelfSeen(message);
  if (opts.tempId && opts.isCurrentChannel) {
    const idx = list.findIndex((x) => x.optimistic && x.id === opts.tempId);
    if (idx !== -1) {
      const copy = [...list];
      copy[idx] = { ...nextMsg, optimistic: false };
      return copy;
    }
  }
  // Also guard against duplicated server IDs already in list
  if (msgId && list.some((m) => m.id === msgId)) {
    return list;
  }
  return [...list, nextMsg];
}

/**
 * Records that `userId` has read the channel. We render a single receipt, on the newest message only, and
 * never on a message the reader wrote themselves.
 */
export function applySeen(
  list: Msg[],
  seen: { userId: string; name?: string },
  stripSelfSeen: StripSelfSeen,
): Msg[] {
  const lastIdx = list.length - 1;
  if (lastIdx < 0) return list;
  const latest = list[lastIdx];
  if (latest && latest.authorId && latest.authorId === seen.userId) return list;
  const copy = [...list];
  const sbIds = new Set(copy[lastIdx].seenByIds || []);
  sbIds.add(seen.userId);
  // keep legacy names as best effort
  const sb = new Set(copy[lastIdx].seenBy || []);
  const nm = (seen.name && seen.name.trim()) ? seen.name : (seen.userId || "Someone");
  if (nm) sb.add(nm);
  copy[lastIdx] = stripSelfSeen({ ...copy[lastIdx], seenBy: Array.from(sb), seenByIds: Array.from(sbIds) });
  return copy;
}

/** Applies an edit. Returns the same list when the message is not in it. */
export function applyEdited(
  list: Msg[],
  edit: { messageId: string; content: string; updatedAt?: string },
): Msg[] {
  const idx = list.findIndex((m) => m.id === edit.messageId);
  if (idx === -1) return list;
  const copy = [...list];
  copy[idx] = { ...copy[idx], content: edit.content, optimistic: false, updatedAt: edit.updatedAt || new Date().toISOString() };
  return copy;
}

/** Removes a message (always returns a new list). */
export function applyDeleted(list: Msg[], messageId: string): Msg[] {
  return list.filter((m) => m.id !== messageId);
}

/**
 * Updates a message's reaction counts from the server, keeping this user's own "mine" flags.
 * Returns the same list when the message is not in it.
 */
export function applyReactions(
  list: Msg[],
  update: { messageId: string; reactions: Record<string, { count: number }> },
): Msg[] {
  const idx = list.findIndex((m) => m.id === update.messageId);
  if (idx === -1) return list;
  const copy = [...list];
  const prev = copy[idx].reactions || {};
  const next: Record<string, { count: number; mine?: boolean }> = {};
  // preserve mine flags while updating counts
  for (const key of Object.keys(update.reactions)) {
    next[key] = { count: update.reactions[key].count, mine: prev[key]?.mine };
  }
  copy[idx] = { ...copy[idx], reactions: next };
  return copy;
}

// ---------------------------------------------------------------------------
// Backlog
// ---------------------------------------------------------------------------

/**
 * Works out what to keep from a loaded backlog: direct-message spaces show only the newest page; the
 * oldest kept message id (the paging cursor); and whether there may be older messages (the server sent a
 * full page).
 */
export function normalizeBacklog(
  messages: Msg[],
  voidId: string,
  pageSize: number,
): { sliced: Msg[]; earliestId: string | null; hasMore: boolean } {
  const isDmSpace = String(voidId || "").startsWith("dm_");
  const sliced = (isDmSpace && messages.length > pageSize) ? messages.slice(-pageSize) : messages;
  return {
    sliced,
    earliestId: sliced[0]?.id || null,
    hasMore: messages.length >= pageSize,
  };
}
